import { useAutoAnimate } from "@formkit/auto-animate/react";
import { StackIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React, { ReactElement } from "react";
import { useHistory } from "react-router-dom";
import { Membership } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { makeMembershipItem } from "../../../models/CreatePurchaseState";
import {
    CreatePurchaseItemState,
    CreatePurchaseStep,
    useCreatePurchaseStore,
} from "../../../stores/createPurchaseStore";
import { sortBy } from "../../../utils/locale";
import { Currency } from "../../Common/Currency";
import { NoResults } from "../../Common/NoResults";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { invalidateArrivalCache } from "../../DatePicker/useArrivalTimeslots";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";

export const MembershipList = ({ memberships }: { memberships: Membership[] }) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const sortedMemberships = sortBy(memberships, "name", false);

    return (
        <div className="px-0 py-10">
            <div className="text-lg font-bold">Memberships</div>
            {sortedMemberships.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div
                    ref={animationParent}
                    className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2"
                    data-testid="membership-list"
                >
                    {sortedMemberships.map((membership) => (
                        <MembershipListItem key={membership.id} membership={membership} />
                    ))}
                </div>
            )}
        </div>
    );
};

const MembershipListItem = ({ membership }: { membership: Membership }) => {
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const seller = useSeller();
    const isAddingItem = history.location.state?.isAddingItem === true;
    const { resetState, addItem, setSeller, setCurrency } = useCreatePurchaseStore.getState().actions;

    const selectedOrganizer = useCreatePurchaseStore((state) => state.organizer);
    const hasAnyItems = useCreatePurchaseStore((state) => state.purchase.items.length > 0);

    const handleMembershipSelect = () => {
        if (!isAddingItem && (!selectedOrganizer || hasAnyItems)) {
            resetState();
            invalidateArrivalCache();
        }

        setSeller(membership.seller.id);
        addItem(makeMembershipItem({ membership }) as CreatePurchaseItemState);
        setCurrency(seller.currency);
        useCustomerDetailsStore.setState({ emailRequired: true });
        useCreatePurchaseStore.setState({
            /** If user already has any items in the cart, and tries to add a new membership, it would be a MIST checkout. So rather than redirecting him to `CHECKOUT` step, we'll redirect to `SELECT_DATE` */
            step: hasAnyItems ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
        });
        history.push("/cart");
    };

    return <MembershipTile membership={membership} onClick={handleMembershipSelect} />;
};

interface MembershipTileProps {
    membership: Membership;
    onClick: () => void;
}

export const MembershipTile = ({ membership, onClick }: MembershipTileProps) => {
    return (
        <div
            className="flex cursor-pointer flex-row rounded py-2 hover:bg-secondary-hover"
            data-testid="membership-list-item"
            onClick={onClick}
        >
            <ProductImage product={membership} />
            <div className="flex flex-col space-y-1">
                <NoTranslate className="text-md font-bold">{membership.name}</NoTranslate>
                <div className="space-x-2">
                    <ProductBadge text={<Currency>{membership.price}</Currency>} icon={<StackIcon />} />
                </div>
            </div>
        </div>
    );
};

interface ProductBadgeProps {
    color?: string;
    text: string | ReactElement;
    icon?: ReactElement;
}

export const ProductBadge = ({ color = "secondary", text, icon }: ProductBadgeProps) => {
    const mediumIcon = icon ? React.cloneElement(icon, { size: "medium" }) : undefined;
    return (
        <Badge color={color} size="medium" icon={mediumIcon}>
            {text}
        </Badge>
    );
};
